// redux/floorPlanSlice.js
import { createSlice, createSelector } from '@reduxjs/toolkit';
import { generateUniqueId } from '../utils/generateUniqueId';
import { setActiveTab, togglePafta, LEGACY_TO_CODE } from './projectTabsSlice';

// Backend code → legacy tab ID (ters eşleme)
const CODE_TO_LEGACY = Object.fromEntries(
  Object.entries(LEGACY_TO_CODE).map(([legacy, code]) => [code, legacy])
);

// Kat türü sabitleri
export const FLOOR_TYPES = {
  BASEMENT: 'basement',      // Bodrum Kat
  GROUND: 'ground',          // Zemin Kat
  MEZZANINE: 'mezzanine',    // Asma Kat
  GALLERY: 'gallery',        // Galeri Kat
  NORMAL: 'normal',          // Normal Kat
  ROOF: 'roof',              // Çatı Kat
};

// floorCount düzenlenebilirlik kuralları
export const EDITABLE_FLOOR_TYPES = {
  [FLOOR_TYPES.NORMAL]: { min: 1, max: 20 },
  [FLOOR_TYPES.BASEMENT]: { min: 1, max: 6 },
};

// Boş tab state (selector fallback)
const EMPTY_TAB_STATE = Object.freeze({ byId: {}, allIds: [], activeId: null });

const createEmptyTab = () => ({ byId: {}, allIds: [], activeId: null });

const initialState = {
  activeTabId: 'mimari',
  // projectTabs.enabledPaftaIds'i takip etmek için (togglePafta sync)
  _enabledPaftaIds: ['mimari'],
  tabs: {
    mimari: createEmptyTab(),
  },
};

const coerceToPlan = (payload) => {
  const id = payload.id ?? generateUniqueId();
  return {
    id,
    name: payload.name ?? 'Unnamed Floor',
    type: payload.type ?? 'floor',
    number: payload.number ?? 0,
    floorCount: payload.floorCount ?? 1,
    draw: Array.isArray(payload.draw) ? payload.draw : [],
    width: payload.width ?? 1800,
    height: payload.height ?? 1300,
    objects: payload.objects ?? [],
    objectsIds: payload.objectsIds ?? [],
    group: payload.group ?? null,
    sourceFloorPlanId: payload.sourceFloorPlanId ?? null,
    // Phase A: Tesisat ve wire ID'leri her kat planına ait olarak burada tutulur.
    // Render katmanı (Phase B) bu listelerden tesisat mesh'lerini floor plan
    // grubunun child'ı olarak inşa edecek; etkileşim (Phase C) ID listelerini
    // creation/delete hook'larından senkron tutacak.
    tesisatIds: Array.isArray(payload.tesisatIds) ? payload.tesisatIds : [],
    wireIds: Array.isArray(payload.wireIds) ? payload.wireIds : [],
  };
};

// [SAVE-LOAD-REWRITE] Mimari'den disipline kat planı kopyala (derin kopya).
// addFloorPlan reducer'ından OTOMATİK çağrılmaz — kullanıcı "Oluştur"a
// bastığında propagateMimariPlansToTabs reducer'ı içinden explicit çağrılır.
const copyPlanToTab = (tab, sourcePlan) => {
  const copyId = generateUniqueId();
  const copy = {
    ...sourcePlan,
    id: copyId,
    sourceFloorPlanId: sourcePlan.id,
    draw: sourcePlan.draw ? JSON.parse(JSON.stringify(sourcePlan.draw)) : [],
    objectsIds: [...(sourcePlan.objectsIds || [])],
    objects: [],
    group: sourcePlan.group || null,
    // Phase A: Tesisat/wire ID'leri tab başına bağımsız (mimari'den kopyalanan
    // plan'da boş başlar; elektrik'te kullanıcı kendi tesisatını ekler).
    tesisatIds: [],
    wireIds: [],
  };
  tab.byId[copyId] = copy;
  tab.allIds.push(copyId);
  if (!tab.activeId) tab.activeId = copyId;
};

// Backend code → legacy tab ID çözümle (floorPlanSlice her zaman legacy key kullanır)
const toLegacyTabId = (tabId) => CODE_TO_LEGACY[tabId] || tabId;

// Helper: tab state'i garantile
const ensureTab = (state, tabId) => {
  const key = toLegacyTabId(tabId);
  if (!state.tabs[key]) {
    state.tabs[key] = createEmptyTab();
  }
  return state.tabs[key];
};

// Helper: aktif tab state'i al
const getActiveTab = (state) => {
  return ensureTab(state, state.activeTabId);
};

// [SAVE-LOAD-REWRITE] Şu an kullanılmıyor — tab değişiminde otomatik
// kopya yapılmıyor. handleTabChange artık boş tab yaratıyor. Gelecek
// için saklandı (örn. legacy proje import senaryosu).
// eslint-disable-next-line no-unused-vars
const initTabFromMimari = (state, tabId) => {
  const newTab = createEmptyTab();
  const mimariTab = state.tabs.mimari;
  if (mimariTab && mimariTab.allIds.length > 0) {
    mimariTab.allIds.forEach(origId => {
      const orig = mimariTab.byId[origId];
      if (orig) copyPlanToTab(newTab, orig);
    });
  }
  state.tabs[tabId] = newTab;
  return newTab;
};

const floorPlanSlice = createSlice({
  name: 'floorPlans',
  initialState,
  reducers: {
    // Ekle — sadece aktif tab'a ekler.
    // [SAVE-LOAD-REWRITE] Otomatik cross-tab kopyalama KALDIRILDI.
    // Yeni mimari: Kat planları proje seviyesi varlık, mimari tab'da tutulur.
    // Disiplinler (electrical/weak_current/...) kendi kat planı listesi
    // taşımaz. Bir disiplinde kat planı kullanılırsa belge (doküman) olarak
    // eklenir — doküman listesi gibi düşünülmelidir. Otomatik dahil etmek
    // mimari sızıntısı yaratıyordu (kullanıcı seçmediği kat planı elektrik
    // paftasında otomatik gözüküyordu).
    addFloorPlan: (state, action) => {
      const tab = getActiveTab(state);
      const plan = coerceToPlan(action.payload || {});
      if (!tab.byId[plan.id]) {
        tab.byId[plan.id] = plan;
        tab.allIds.push(plan.id);
      }
      if (!tab.activeId) tab.activeId = plan.id;
    },

    // Birden fazla ekle — sadece aktif tab'a (cross-tab kopya yok).
    addFloorPlans: (state, action) => {
      const tab = getActiveTab(state);
      const plans = Array.isArray(action.payload) ? action.payload : [];
      const coercedPlans = plans.map(p => coerceToPlan(p));
      coercedPlans.forEach((plan) => {
        if (!tab.byId[plan.id]) {
          tab.byId[plan.id] = plan;
          tab.allIds.push(plan.id);
        }
      });
      if (!tab.activeId && tab.allIds.length > 0) {
        tab.activeId = tab.allIds[0];
      }
    },

    // Sil — sadece aktif tab'dan siler
    removeFloorPlan: (state, action) => {
      const tab = getActiveTab(state);
      const id = action.payload;
      if (tab.byId[id]) {
        delete tab.byId[id];
        tab.allIds = tab.allIds.filter((x) => x !== id);
        if (tab.activeId === id) {
          tab.activeId = tab.allIds[0] ?? null;
        }
      }
    },

    // Güncelle — belirtilen veya aktif tab'daki planı günceller
    updateFloorPlan: (state, action) => {
      const { id, changes, tabId } = action.payload || {};
      const tab = tabId ? ensureTab(state, tabId) : getActiveTab(state);
      if (!id || !tab.byId[id]) return;
      const plan = tab.byId[id];
      Object.assign(plan, changes);
    },

    // Tüm tab'lardaki linked plan'ları aynı anda günceller. Mimari ve
    // elektrik/telekom/yangin kopyalarının yapısal alanları (örn. floorCount)
    // senkronize tutulmalı — yoksa Kat Planı Ayarları sadece mimari'yi günceller,
    // elektrik tab'ı eski değerle kalır → YC/Kolon Şeması floor expansion yapamaz.
    //
    // Eşleme: aynı plan zinciri = (id eşit) VEYA (sourceFloorPlanId === id) VEYA
    // (her ikisinin sourceFloorPlanId'si eşit). İlk geçen tab'da source ID'yi bul,
    // sonra tüm tab'larda eşleşen plan'lara changes uygula.
    updateFloorPlanAcrossTabs: (state, action) => {
      const { id, changes } = action.payload || {};
      if (!id) return;

      // Source ID bul: id ya doğrudan source'tur ya da kopyanın sourceFloorPlanId'si
      let sourceId = id;
      for (const tabKey of Object.keys(state.tabs)) {
        const plan = state.tabs[tabKey]?.byId?.[id];
        if (plan) {
          sourceId = plan.sourceFloorPlanId || plan.id;
          break;
        }
      }

      // Tüm tab'larda: id === sourceId VEYA sourceFloorPlanId === sourceId
      for (const tabKey of Object.keys(state.tabs)) {
        const tab = state.tabs[tabKey];
        if (!tab) continue;
        for (const pid of tab.allIds) {
          const plan = tab.byId[pid];
          if (!plan) continue;
          if (plan.id === sourceId || plan.sourceFloorPlanId === sourceId) {
            Object.assign(plan, changes);
          }
        }
      }
    },

    // Aktif kat planını ayarla
    setActiveFloorPlan: (state, action) => {
      const tab = getActiveTab(state);
      const id = action.payload;
      if (tab.byId[id]) tab.activeId = id;
    },

    // Kat planını aktif tab'da kopyala (duplicate)
    duplicateFloorPlan: (state, action) => {
      const tab = getActiveTab(state);
      const sourceId = action.payload;
      const source = tab.byId[sourceId];
      if (!source) return;

      const copyId = generateUniqueId();
      const copy = {
        ...source,
        id: copyId,
        sourceFloorPlanId: source.sourceFloorPlanId || source.id,
        draw: source.draw ? JSON.parse(JSON.stringify(source.draw)) : [],
        objectsIds: [...(source.objectsIds || [])],
        objects: [],
        group: null,
        name: `${source.name} (kopya)`,
      };
      tab.byId[copyId] = copy;
      tab.allIds.push(copyId);
      tab.activeId = copyId;
    },

    // DRAW yönetimi
    addDrawItem: (state, action) => {
      const tab = getActiveTab(state);
      const { id, item } = action.payload || {};
      if (!id || !tab.byId[id]) return;
      tab.byId[id].draw.push(item);
    },

    removeDrawItemByIndex: (state, action) => {
      const tab = getActiveTab(state);
      const { id, index } = action.payload || {};
      if (!id || !tab.byId[id]) return;
      const arr = tab.byId[id].draw;
      if (Array.isArray(arr) && index >= 0 && index < arr.length) {
        arr.splice(index, 1);
      }
    },

    removeDrawItemByPredicate: (state, action) => {
      const tab = getActiveTab(state);
      const { id, predicate } = action.payload || {};
      if (!id || !tab.byId[id] || typeof predicate !== 'function') return;
      tab.byId[id].draw = tab.byId[id].draw.filter((d) => !predicate(d));
    },

    replaceDraw: (state, action) => {
      const tab = getActiveTab(state);
      const { id, draw } = action.payload || {};
      if (!id || !tab.byId[id] || !Array.isArray(draw)) return;
      tab.byId[id].draw = draw;
    },

    clearDraw: (state, action) => {
      const tab = getActiveTab(state);
      const id = action.payload;
      if (!id || !tab.byId[id]) return;
      tab.byId[id].draw = [];
    },

    // Aktif tab'ın tüm kat planlarını temizle
    clearAllFloorPlans: (state) => {
      const tab = getActiveTab(state);
      tab.byId = {};
      tab.allIds = [];
      tab.activeId = null;
    },

    // [SAVE-LOAD-REWRITE] "Oluştur" butonu — mimari'deki tüm kat planlarını
    // belirtilen disiplin tab'larına KOPYALA.
    // payload: { targetTabIds: string[] } — kopyalanacak hedef tab listesi.
    // Idempotent: hedef tab'da aynı sourceFloorPlanId zaten varsa skip eder.
    // Bu sayede kullanıcı "Oluştur"u birden çok kez tıklarsa duplicate olmaz.
    propagateMimariPlansToTabs: (state, action) => {
      const { targetTabIds } = action.payload || {};
      if (!Array.isArray(targetTabIds) || targetTabIds.length === 0) return;
      const mimariTab = state.tabs.mimari;
      if (!mimariTab || mimariTab.allIds.length === 0) return;

      for (const rawTabId of targetTabIds) {
        const targetTabId = toLegacyTabId(rawTabId);
        if (targetTabId === 'mimari') continue;
        const targetTab = ensureTab(state, targetTabId);
        for (const origId of mimariTab.allIds) {
          const orig = mimariTab.byId[origId];
          if (!orig) continue;
          // Idempotent: aynı kaynaktan zaten kopyalanmış mı?
          const alreadyCopied = targetTab.allIds.some(id => {
            const p = targetTab.byId[id];
            return p?.sourceFloorPlanId === origId;
          });
          if (alreadyCopied) continue;
          copyPlanToTab(targetTab, orig);
        }
      }
    },

    // ── Phase A: Tesisat/Wire ID listesi reducer'ları ────────────────────
    // Tesisat (priz, anahtar, lamba, panel, makine, supplyPoint, kablo bacası)
    // ve wire (kablo) ID'leri kat planına ait olarak burada tutulur.
    // Veri kendisi (tesisatSlice/wireSlice) hâlâ ayrı slice'larda; bu listeler
    // hangi tesisat/wire'ın hangi kat planına ait olduğunu söyleyen INDEX'tir.
    // Phase B render katmanı bu listelerden mesh'leri floor plan group'unun
    // child'ı olarak inşa edecek.

    addTesisatIdToFloorPlan: (state, action) => {
      const { tabId, floorPlanId, itemId } = action.payload || {};
      if (!tabId || !floorPlanId || !itemId) return;
      const tab = ensureTab(state, tabId);
      const plan = tab.byId[floorPlanId];
      if (!plan) return;
      if (!Array.isArray(plan.tesisatIds)) plan.tesisatIds = [];
      if (!plan.tesisatIds.includes(itemId)) plan.tesisatIds.push(itemId);
    },

    removeTesisatIdFromFloorPlan: (state, action) => {
      const { tabId, floorPlanId, itemId } = action.payload || {};
      if (!tabId || !itemId) return;
      const tab = ensureTab(state, tabId);
      const targetId = floorPlanId;
      // Belirtilen plan'dan sil; floorPlanId yoksa tüm planlardan sil (defansif)
      const planIds = targetId ? [targetId] : tab.allIds;
      for (const pid of planIds) {
        const plan = tab.byId[pid];
        if (!plan || !Array.isArray(plan.tesisatIds)) continue;
        plan.tesisatIds = plan.tesisatIds.filter(id => id !== itemId);
      }
    },

    addWireIdToFloorPlan: (state, action) => {
      const { tabId, floorPlanId, itemId } = action.payload || {};
      if (!tabId || !floorPlanId || !itemId) return;
      const tab = ensureTab(state, tabId);
      const plan = tab.byId[floorPlanId];
      if (!plan) return;
      if (!Array.isArray(plan.wireIds)) plan.wireIds = [];
      if (!plan.wireIds.includes(itemId)) plan.wireIds.push(itemId);
    },

    removeWireIdFromFloorPlan: (state, action) => {
      const { tabId, floorPlanId, itemId } = action.payload || {};
      if (!tabId || !itemId) return;
      const tab = ensureTab(state, tabId);
      const targetId = floorPlanId;
      const planIds = targetId ? [targetId] : tab.allIds;
      for (const pid of planIds) {
        const plan = tab.byId[pid];
        if (!plan || !Array.isArray(plan.wireIds)) continue;
        plan.wireIds = plan.wireIds.filter(id => id !== itemId);
      }
    },

    // Migration: mevcut tesisat/wire'ları floor plan'a re-index et (JBZ load
    // sonrası, başlangıçta tesisatIds/wireIds boşken çalışır).
    rebuildFloorPlanItemIndex: (state, action) => {
      const { tabId, tesisatItems, wireItems } = action.payload || {};
      if (!tabId) return;
      const tab = ensureTab(state, tabId);
      // Tüm planların listelerini sıfırla
      for (const pid of tab.allIds) {
        if (tab.byId[pid]) {
          tab.byId[pid].tesisatIds = [];
          tab.byId[pid].wireIds = [];
        }
      }
      // Tesisat'ları indeksle
      if (Array.isArray(tesisatItems)) {
        for (const item of tesisatItems) {
          if (!item?.id || !item?.floorPlanId) continue;
          const plan = tab.byId[item.floorPlanId];
          if (!plan) continue;
          if (!Array.isArray(plan.tesisatIds)) plan.tesisatIds = [];
          if (!plan.tesisatIds.includes(item.id)) plan.tesisatIds.push(item.id);
        }
      }
      // Wire'ları indeksle
      if (Array.isArray(wireItems)) {
        for (const item of wireItems) {
          if (!item?.id || !item?.floorPlanId) continue;
          const plan = tab.byId[item.floorPlanId];
          if (!plan) continue;
          if (!Array.isArray(plan.wireIds)) plan.wireIds = [];
          if (!plan.wireIds.includes(item.id)) plan.wireIds.push(item.id);
        }
      }
    },
  },
  extraReducers: (builder) => {
    // Ortak tab değiştirme mantığı
    // [SAVE-LOAD-REWRITE] Mimari'den otomatik kopya KALDIRILDI.
    // Disiplinler kendi kat planı listesi tutmaz; tab BOŞ açılır.
    const handleTabChange = (state, newTabId) => {
      const legacyId = toLegacyTabId(newTabId);
      state.activeTabId = legacyId;
      // Tab yoksa boş olarak yarat — mimari'den kopyalanmaz
      if (!state.tabs[legacyId]) {
        state.tabs[legacyId] = createEmptyTab();
      }
    };

    // setActiveTab: doğrudan tab seçimi
    builder.addCase(setActiveTab, (state, action) => {
      handleTabChange(state, action.payload);
    });

    // togglePafta: projectTabsSlice'taki enable/disable mantığını aynalayarak sync
    builder.addCase(togglePafta, (state, action) => {
      const id = action.payload;
      if (id === 'mimari') return;

      const legacyId = toLegacyTabId(id);
      const enabledIds = state._enabledPaftaIds || ['mimari'];
      const idx = enabledIds.indexOf(legacyId);

      if (idx === -1) {
        // Pafta etkinleştiriliyor → aktif tab bu pafta olur
        enabledIds.push(legacyId);
        handleTabChange(state, legacyId);
      } else {
        // Pafta devre dışı bırakılıyor
        enabledIds.splice(idx, 1);
        if (state.activeTabId === legacyId) {
          handleTabChange(state, 'mimari');
        }
      }
      state._enabledPaftaIds = enabledIds;
    });
  },
});

export const {
  addFloorPlan,
  addFloorPlans,
  removeFloorPlan,
  updateFloorPlan,
  updateFloorPlanAcrossTabs,
  setActiveFloorPlan,
  duplicateFloorPlan,
  addDrawItem,
  removeDrawItemByIndex,
  removeDrawItemByPredicate,
  replaceDraw,
  clearDraw,
  clearAllFloorPlans,
  propagateMimariPlansToTabs,
  // Phase A: tesisat/wire ID indeksleme
  addTesisatIdToFloorPlan,
  removeTesisatIdFromFloorPlan,
  addWireIdToFloorPlan,
  removeWireIdFromFloorPlan,
  rebuildFloorPlanItemIndex,
} = floorPlanSlice.actions;

export default floorPlanSlice.reducer;

// ---------- Selectors ----------

// Aktif tab'ın floor plan sub-state'ini döndürür (eski state şekliyle uyumlu)
// Backend code → legacy key çözümleme: strong_current → elektrik vb.
export const selectFloorPlanState = (state) => {
  const tabId = state.projectTabs.activeTabId;
  const tabs = state.floorPlans.tabs;
  if (tabs?.[tabId]) return tabs[tabId];
  // Backend code ise legacy key'e çevir
  const legacyId = CODE_TO_LEGACY[tabId];
  if (legacyId && tabs?.[legacyId]) return tabs[legacyId];
  return EMPTY_TAB_STATE;
};

// Tüm tabs verisini döndürür (serialization için)
export const selectFloorPlanTabs = (state) => state.floorPlans.tabs;

const selectFloorPlanAllIds = (state) => selectFloorPlanState(state).allIds;
const selectFloorPlanByIdObject = (state) => selectFloorPlanState(state).byId;

export const selectAllFloorPlans = createSelector(
  [selectFloorPlanAllIds, selectFloorPlanByIdObject],
  (allIds, byId) => allIds.map((id) => byId[id])
);

export const selectFloorPlanById = (state, id) =>
  selectFloorPlanState(state).byId[id] ?? null;

export const selectActiveFloorPlanId = (state) =>
  selectFloorPlanState(state).activeId;

export const selectActiveFloorPlan = (state) => {
  const s = selectFloorPlanState(state);
  return s.activeId ? s.byId[s.activeId] : null;
};

export const selectActiveDraw = (state) => {
  const plan = selectActiveFloorPlan(state);
  return plan ? plan.draw : [];
};

// Mimari tab'ın floor plan'larını döndürür (propagation kontrolleri için)
export const selectMimariFloorPlans = (state) => {
  const mimariTab = state.floorPlans.tabs?.mimari || EMPTY_TAB_STATE;
  return mimariTab.allIds.map(id => mimariTab.byId[id]);
};

// ─────────────────────────────────────────────────────────────────────────────
// Mesh-FP mimarisi (bkz. docs/mesh-fp-mimari.md)
//
// Tüm fp meshleri proje genelinde **aynı boyutta** çizilir — boyut = mimari
// tab'daki en büyük kat planının (width, height)'ı. Bu sayede çocuk nesnelerin
// (mimari DXF + tesisat + wire) mesh-local koordinatları proje boyunca tutarlı
// kalır; pafta'lar arası kopyalama veya fp seçimi değiştiğinde koordinatlar
// kayma yaşamaz.
//
// Hiçbir mimari fp yoksa { width: 0, height: 0 } döner — sahne katmanı bu
// durumda mesh oluşturmaz.
//
// Input selector'lar slice-state'ten DOĞRUDAN reference döner (state immutable
// → aynı state'te aynı reference). selectMimariFloorPlans gibi türetilmiş
// (her çağrıda .map ile yeni array) input kullanılırsa reselect input
// stability'sini bozar → cache invalidate → kısır döngü.
// ─────────────────────────────────────────────────────────────────────────────
export const selectProjectMeshSize = createSelector(
  [
    (state) => state.floorPlans.tabs?.mimari?.byId,
    (state) => state.floorPlans.tabs?.mimari?.allIds,
  ],
  (byId, allIds) => {
    if (!allIds?.length || !byId) return { width: 0, height: 0 };
    let w = 0;
    let h = 0;
    for (const id of allIds) {
      const p = byId[id];
      if (!p) continue;
      if ((p.width || 0) > w) w = p.width;
      if ((p.height || 0) > h) h = p.height;
    }
    return { width: w, height: h };
  }
);

// ========== Kat Türü Selector'ları ==========

export const getFloorSortOrder = (type, number) => {
  switch (type) {
    case FLOOR_TYPES.BASEMENT:
      return number;
    case FLOOR_TYPES.GROUND:
      return 0;
    case FLOOR_TYPES.MEZZANINE:
      return 0.1;
    case FLOOR_TYPES.GALLERY:
      return 0.2;
    case FLOOR_TYPES.NORMAL:
      return number;
    case FLOOR_TYPES.ROOF:
      return 9999;
    default:
      return 0;
  }
};

export const selectHasGroundFloor = createSelector(
  [selectAllFloorPlans],
  (plans) => plans.some(p => p.type === FLOOR_TYPES.GROUND)
);

export const selectHasMezzanine = createSelector(
  [selectAllFloorPlans],
  (plans) => plans.some(p => p.type === FLOOR_TYPES.MEZZANINE)
);

export const selectHasGallery = createSelector(
  [selectAllFloorPlans],
  (plans) => plans.some(p => p.type === FLOOR_TYPES.GALLERY)
);

export const selectHasRoof = createSelector(
  [selectAllFloorPlans],
  (plans) => plans.some(p => p.type === FLOOR_TYPES.ROOF)
);

export const selectBasementCount = createSelector(
  [selectAllFloorPlans],
  (plans) => plans
    .filter(p => p.type === FLOOR_TYPES.BASEMENT)
    .reduce((sum, p) => sum + (p.floorCount || 1), 0)
);

export const selectNormalFloorCount = createSelector(
  [selectAllFloorPlans],
  (plans) => plans
    .filter(p => p.type === FLOOR_TYPES.NORMAL)
    .reduce((sum, p) => sum + (p.floorCount || 1), 0)
);

export const selectTotalRealFloorCount = createSelector(
  [selectAllFloorPlans],
  (plans) => plans.reduce((sum, p) => sum + (p.floorCount || 1), 0)
);

export function getRepresentedFloors(plan) {
  const count = plan.floorCount || 1;
  const type = plan.type;

  if (type === FLOOR_TYPES.NORMAL) {
    const startNumber = plan.number || 1;
    return Array.from({ length: count }, (_, i) => `${startNumber + i}.Normal Kat`);
  }
  if (type === FLOOR_TYPES.BASEMENT) {
    const startNumber = Math.abs(plan.number) || 1;
    return Array.from({ length: count }, (_, i) => `-${startNumber + i}.Bodrum Kat`);
  }
  switch (type) {
    case FLOOR_TYPES.GROUND: return ['Zemin Kat'];
    case FLOOR_TYPES.MEZZANINE: return ['Asma Kat'];
    case FLOOR_TYPES.GALLERY: return ['Galeri Kat'];
    case FLOOR_TYPES.ROOF: return ['Çatı Kat'];
    default: return [plan.name || 'Kat'];
  }
}

export const selectSortedFloorPlans = createSelector(
  [selectAllFloorPlans],
  (plans) => [...plans].sort((a, b) => {
    const orderA = getFloorSortOrder(a.type, a.number);
    const orderB = getFloorSortOrder(b.type, b.number);
    return orderA - orderB;
  })
);

// ── Phase A: Tesisat/Wire ID indeksi selector'ları ─────────────────────────
// Belirli bir tab'daki kat planlarının tesisatIds/wireIds toplamı.

export const selectFloorPlanTesisatIds = (state, tabId, floorPlanId) => {
  const tab = state.floorPlans.tabs?.[tabId];
  return tab?.byId?.[floorPlanId]?.tesisatIds || [];
};

export const selectFloorPlanWireIds = (state, tabId, floorPlanId) => {
  const tab = state.floorPlans.tabs?.[tabId];
  return tab?.byId?.[floorPlanId]?.wireIds || [];
};

// Bir tesisat/wire ID'sinin hangi kat planına ait olduğunu bul
export const findFloorPlanIdByTesisatId = (state, tabId, itemId) => {
  const tab = state.floorPlans.tabs?.[tabId];
  if (!tab) return null;
  for (const pid of tab.allIds) {
    if (tab.byId[pid]?.tesisatIds?.includes(itemId)) return pid;
  }
  return null;
};

export const findFloorPlanIdByWireId = (state, tabId, itemId) => {
  const tab = state.floorPlans.tabs?.[tabId];
  if (!tab) return null;
  for (const pid of tab.allIds) {
    if (tab.byId[pid]?.wireIds?.includes(itemId)) return pid;
  }
  return null;
};
